// =============================================================================
// src/features/explore/explore-card.ts — HTML do card de drill-in (municipio/zona).
//
// Proveniencia: assets/explore.js `munCardHTML` (559-576), `zoneCardHTML` (577-587),
//   `metricLine` (588-596) e `fmt` (597). Portado FIEL: a estrutura/classes do card
//   (exp-card-head/row/cta, data-card-action) e a formatacao "2,2 mi"/"320 mil" sao
//   paridade de marcacao — NAO alterar. Recebe a metrica/ano via parametros (sem ler
//   o estado global do mapa), mantendo SRP.
//
// SRP: so geracao de string HTML. Sem DOM, sem Leaflet.
// =============================================================================

import type {
  MunicipalityId,
  ZonaKey,
  ElectoralYear,
  ExploreThematicAxis,
  ExploreMunicipality,
} from '../../app/types';
import {
  EXPLORE_DATASET,
  MANAUS_ZONA_DISPLAY_NAME,
  MANAUS_ZONA_POP_DATA,
  MANAUS_ELECTORAL_BY_YEAR,
} from '../../data';
import { METRIC_META, BUCKET_LABELS, THEME_METRICS, type ExploreColorMetric } from './constants';
import { buildExploreProvenanceFooterMarkup } from '../../data/data-provenance';
import { exploreLiveSource } from './explore-live-hydrate';
import {
  thematicLevelOf,
  volatilidadeOf,
  variacao2422DeltaOf,
  campoBatalhaPartsOf,
} from './explore-helpers';

/** Type guard: a metrica e um dos 7 eixos tematicos (tem nivel 0..3 por municipio). */
function isThematicAxis(metric: ExploreColorMetric): metric is ExploreThematicAxis {
  return (THEME_METRICS as readonly string[]).includes(metric);
}

/** Linha extra de metrica no card (so quando colorBy != partido). */
interface MetricLine {
  readonly k: string;
  readonly v: string;
}

/** Formata milhares -> "2,2 mi"/"320 mil" (assets/explore.js fmt, linha 597). */
export function formatThousands(thousands: number | null | undefined): string {
  if (thousands == null) return '—';
  return thousands >= 1000
    ? `${(thousands / 1000).toFixed(1).replace('.', ',')} mi`
    : `${thousands} mil`;
}

/** Direção textual + magnitude (pp) da variação 24×22 para o card. */
function variacaoLine(mun: ExploreMunicipality): string {
  const delta = variacao2422DeltaOf(mun);
  const dir = delta > 0 ? 'subiu' : delta < 0 ? 'caiu' : 'estável';
  return `${dir} ${Math.abs(delta).toFixed(1).replace('.', ',')}pp`;
}

/** Linha "Campo de batalha": índice 0..100 + decomposição das 3 contribuições. */
function campoBatalhaLine(mun: ExploreMunicipality): string {
  const parts = campoBatalhaPartsOf(mun);
  const n = (value: number): number => Math.round(value * 100);
  return `${n(parts.indice)}/100 · disputa ${n(parts.cDisputa)} · porte ${n(parts.cPeso)} · volat. ${n(parts.cVolatilidade)}`;
}

/**
 * Linha de metrica extra do card por colorBy (assets/explore.js 588-596). Metricas
 * sem linha propria (nenhum/partido/segundo/competitividade/campanha) caem em null —
 * o 1º/2º colocado ja aparecem no corpo do card.
 * diverge do golden por decisao de produto 2026-05-31 (novas metricas toleradas);
 * +volatilidade/variacao/campoBatalha em 2026-06-01 (indices estrategicos).
 */
function metricLineFor(id: MunicipalityId, colorBy: ExploreColorMetric): MetricLine | null {
  const mun = EXPLORE_DATASET.munis[id];
  if (!mun) return null;
  if (colorBy === 'densidade') return { k: 'Eleitores', v: formatThousands(mun.eleitoresK) };
  if (colorBy === 'populacao') return { k: 'População', v: `${formatThousands(mun.habK)} hab` };
  if (colorBy === 'idh') return { k: 'IDH', v: mun.idh.toFixed(2).replace('.', ',') };
  if (colorBy === 'volatilidade') return { k: 'Volatilidade', v: `${volatilidadeOf(mun)}× em 5 ciclos` };
  if (colorBy === 'variacao2422') return { k: 'Variação 24×22', v: variacaoLine(mun) };
  if (colorBy === 'campoBatalha') return { k: 'Campo de batalha', v: campoBatalhaLine(mun) };
  if (isThematicAxis(colorBy)) return { k: METRIC_META[colorBy]?.label ?? colorBy, v: BUCKET_LABELS[thematicLevelOf(mun, colorBy)] ?? '—' };
  return null;
}

/** Subtitulo do card de municipio (regiao + populacao quando houver). */
function munSubtitle(region: string, habK?: number, eleitoresK?: number): string {
  const pop = habK ? ` · ${formatThousands(habK)} hab · ${formatThousands(eleitoresK)} eleitores` : '';
  return `${region}${pop}`;
}

/** HTML do card de um municipio (assets/explore.js 559-576). */
export function buildMunCardHtml(id: MunicipalityId, year: ElectoralYear, colorBy: ExploreColorMetric): string {
  const mun = EXPLORE_DATASET.munis[id];
  const result = mun?.el[year];
  const lead = result ? `${result.p} (${result.pct}%)` : '—';
  const second = result ? result.s : '—';
  const metric = metricLineFor(id, colorBy);
  const ecoRow = mun?.economia ? `<div class="exp-card-row"><span>Economia</span><strong class="exp-card-eco">${mun.economia}</strong></div>` : '';
  const metricRow = metric ? `<div class="exp-card-row"><span>${metric.k}</span><strong>${metric.v}</strong></div>` : '';
  return (
    `<div class="exp-card-head"><div>` +
    `<div class="exp-card-title">${mun?.name ?? id}</div>` +
    `<div class="exp-card-sub">${munSubtitle(mun?.region ?? '', mun?.habK, mun?.eleitoresK)}</div></div>` +
    `<button class="exp-card-x" type="button" data-card-action="close" aria-label="Fechar"><i class="ti ti-x"></i></button></div>` +
    `<div class="exp-card-rows">` +
    `<div class="exp-card-row"><span>1º colocado (${year})</span><strong>${lead}</strong></div>` +
    `<div class="exp-card-row"><span>2º colocado</span><strong>${second}</strong></div>` +
    ecoRow + metricRow +
    `</div>` +
    `<button class="exp-card-cta" type="button" data-card-action="detail"><i class="ti ti-chart-arcs"></i> Abrir análise detalhada</button>` +
    // Reforma 2026-06 (D3): fonte citada no momento da inspecao do dado.
    // Runtime 2026-06: nomeia a origem resolvida (Hasura ao vivo vs base local).
    `<div class="u-explore-provenance">${buildExploreProvenanceFooterMarkup(exploreLiveSource())}</div>`
  );
}

/** HTML do card de uma zona de Manaus (assets/explore.js 577-587). */
export function buildZoneCardHtml(key: ZonaKey, year: ElectoralYear): string {
  const result = MANAUS_ELECTORAL_BY_YEAR[year]?.[key];
  const pop = MANAUS_ZONA_POP_DATA[key];
  const name = MANAUS_ZONA_DISPLAY_NAME[key] ?? key;
  const popText = pop ? ` · ${pop.hab} hab · ${pop.eleitores} eleitores` : '';
  return (
    `<div class="exp-card-head"><div>` +
    `<div class="exp-card-title">Zona ${name}</div>` +
    `<div class="exp-card-sub">Manaus${popText}</div></div>` +
    `<button class="exp-card-x" type="button" data-card-action="close" aria-label="Fechar"><i class="ti ti-x"></i></button></div>` +
    `<div class="exp-card-rows">` +
    `<div class="exp-card-row"><span>1º colocado</span><strong>${result ? result[2] : '—'}</strong></div>` +
    `<div class="exp-card-row"><span>2º colocado</span><strong>${result ? result[3] : '—'}</strong></div></div>` +
    `<button class="exp-card-cta" type="button" data-card-action="detail"><i class="ti ti-chart-arcs"></i> Abrir análise detalhada</button>` +
    `<div class="u-explore-provenance">${buildExploreProvenanceFooterMarkup(exploreLiveSource())}</div>`
  );
}
